import { SORT_ASC, SORT_DESC } from 'pages/Valuations/util/constants';
import { getNumberValue, getStringValue } from 'utilities';
import {
  ENTERPRISE_VALUE_ID,
  EV_EXCL_OP_LEASES_ALIAS,
  MARKET_CAP_ID,
  STOCK_PRICE_ID,
  TICKER_SYMBOL_ID,
} from '../constants';
import { GPCExtended } from '../GuidelinePublicCompanies.props';

/**
 * Sort keys for Capital IQ Extra Info columns (ticker, stock price, market cap, enterprise value, EV excl. op leases).
 * Must stay aligned with `rowTransformer` sortable columns and `ColumnIndicator` `columnKey` (first field value).
 */
export const CAPITAL_IQ_EXTRA_INFO_SORT_KEYS = [
  TICKER_SYMBOL_ID,
  STOCK_PRICE_ID,
  MARKET_CAP_ID,
  ENTERPRISE_VALUE_ID,
  EV_EXCL_OP_LEASES_ALIAS,
] as const;

export type CapitalIqExtraInfoSortKey = (typeof CAPITAL_IQ_EXTRA_INFO_SORT_KEYS)[number];

const STRING_SORT_KEYS: ReadonlySet<string> = new Set<string>([TICKER_SYMBOL_ID]);

export const isCapitalIqExtraInfoSortKey = (key: string): key is CapitalIqExtraInfoSortKey =>
  (CAPITAL_IQ_EXTRA_INFO_SORT_KEYS as readonly string[]).includes(key);

/**
 * Sort `gpc_comparison` rows by a Capital IQ Extra Info field. `sortDescending` matches spreadsheet behavior
 * after a header click (`true` = descending: larger / later strings first for numbers / localeCompare).
 */
export const sortGpcComparisonsByCapitalIqColumn = (
  comparisons: GPCExtended[],
  sortKey: CapitalIqExtraInfoSortKey,
  sortDescending: boolean
): GPCExtended[] => {
  const isStringColumn = STRING_SORT_KEYS.has(sortKey);
  return [...comparisons].sort((a, b) => {
    const aValue = a?.[sortKey as keyof GPCExtended];
    const bValue = b?.[sortKey as keyof GPCExtended];

    if (!aValue && !bValue) {
      return String(a.cap_iq_id ?? '').localeCompare(String(b.cap_iq_id ?? ''));
    }
    if (!aValue) return 1;
    if (!bValue) return -1;

    let cmp: number;
    if (sortDescending) {
      cmp = isStringColumn
        ? getStringValue(bValue).localeCompare(getStringValue(aValue))
        : getNumberValue(bValue) - getNumberValue(aValue);
    } else {
      cmp = isStringColumn
        ? getStringValue(aValue).localeCompare(getStringValue(bValue))
        : getNumberValue(aValue) - getNumberValue(bValue);
    }
    if (cmp !== 0) {
      return cmp;
    }
    return String(a.cap_iq_id ?? '').localeCompare(String(b.cap_iq_id ?? ''));
  });
};

const sortDirectionIsDescending = (value: string | undefined): boolean =>
  getStringValue(value) === SORT_DESC;

/**
 * Reads active Capital IQ column sort from header state. Prefers keys in `CAPITAL_IQ_EXTRA_INFO_SORT_KEYS` order.
 */
export const getCapitalIqExtraInfoSortFromLastSortedColumn = (
  lastSortedColumn: Record<string, string> | null | undefined
): { sortKey: CapitalIqExtraInfoSortKey; sortDescending: boolean; sortedColumnForUi: Record<string, string> } | null => {
  if (!lastSortedColumn) {
    return null;
  }
  // Own-property check (not `in`, which includes the prototype chain) and not a truthy value check
  // (sort direction may be empty string). `Object.prototype…call` avoids a shadowed `hasOwnProperty` on `lastSortedColumn`.
  const sortKey = (CAPITAL_IQ_EXTRA_INFO_SORT_KEYS as readonly CapitalIqExtraInfoSortKey[]).find(k =>
    Object.prototype.hasOwnProperty.call(lastSortedColumn, k)
  );
  if (sortKey === undefined) {
    return null;
  }
  const raw = lastSortedColumn[sortKey];
  const sortDescending = sortDirectionIsDescending(raw);
  return {
    sortKey,
    sortDescending,
    sortedColumnForUi: { [sortKey]: sortDescending ? SORT_DESC : SORT_ASC },
  };
};

/** Default when no Capital IQ sort is active yet: market cap descending (matches historical GPC default). */
export const getDefaultCapitalIqExtraInfoSort = (): {
  sortKey: CapitalIqExtraInfoSortKey;
  sortDescending: boolean;
  sortedColumnForUi: Record<string, string>;
} => ({
  sortKey: MARKET_CAP_ID,
  sortDescending: true,
  sortedColumnForUi: { [MARKET_CAP_ID]: SORT_DESC },
});

export const resolveCapitalIqExtraInfoSort = (
  lastSortedColumn: Record<string, string> | null | undefined
): {
  sortKey: CapitalIqExtraInfoSortKey;
  sortDescending: boolean;
  sortedColumnForUi: Record<string, string>;
} => getCapitalIqExtraInfoSortFromLastSortedColumn(lastSortedColumn) ?? getDefaultCapitalIqExtraInfoSort();

/**
 * Prefer live `lastSortedColumn` when it includes a Capital IQ Extra Info sort key; otherwise use the last
 * applied sort held in a ref so add-comp and layout effects keep the same column (e.g. ticker) instead
 * of falling back to default market cap when React state is briefly null or out of sync.
 */
export const getEffectiveCapitalIqSortedColumnInput = (
  lastSortedColumn: Record<string, string> | null | undefined,
  fallbackSortedColumn: Record<string, string> | null
): Record<string, string> | null | undefined => {
  if (lastSortedColumn != null && getCapitalIqExtraInfoSortFromLastSortedColumn(lastSortedColumn) != null) {
    return lastSortedColumn;
  }
  return fallbackSortedColumn ?? undefined;
};
